import { RoleName, ScheduleRuleRole, UserStatus } from 'chaika-enums';
import uniq from 'lodash/uniq';
import uniqBy from 'lodash/uniqBy';

import { UserData } from 'chaika-frontend-types/frontend-cms';

import { queryClient } from '@store/queryClient';
import { userQueryOptions, UserListFilters } from '@user/queries/userQueries';
import { scheduleRuleQueryOptions } from '@modules/clinics/queries/scheduleRuleQueries';

interface Params {
    name: string;
    organizationId?: string;
    roleNames?: RoleName[];
}

export async function findClinicUsers({ name, organizationId, roleNames }: Params): Promise<UserData[]> {
    const trimmedName = name.trim();

    if (trimmedName === '') {
        return [];
    }

    const commonFilters: UserListFilters = {
        name: trimmedName,
        roleNames,
        statuses: UserStatus.Active,
        withoutTechnical: true,
    };

    const [mainOrganizationUsers, scheduleRuleUsers] = await Promise.all([
        queryClient.fetchQuery(
            userQueryOptions.list({
                ...commonFilters,
                mainOrganizationId: !!organizationId ? [organizationId] : undefined,
            }),
        ),
        findScheduleRuleUsers(commonFilters, organizationId),
    ]);

    return uniqBy([...mainOrganizationUsers, ...scheduleRuleUsers], ({ id }) => id);
}

async function findScheduleRuleUsers(filters: UserListFilters, organizationId?: string): Promise<UserData[]> {
    if (!organizationId) {
        return [];
    }

    const scheduleRules = await queryClient.fetchQuery(
        scheduleRuleQueryOptions.byClinics([organizationId], ScheduleRuleRole.Doctor),
    );
    const ruleUserIds = uniq(scheduleRules.map(({ userId }) => userId));

    if (ruleUserIds.length === 0) {
        return [];
    }

    return queryClient.fetchQuery(userQueryOptions.list({ ...filters, ids: ruleUserIds }));
}
